import "./Partner.scss";
import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { ProductsColumns, UserRatesColumns } from "../../SourceData";
import StarIcon from "@mui/icons-material/Star";
import FactoriesProductsGif from "./../../assets/factories-products-gif.gif";
import AddressLocation from "./../../assets/location-icon.png";
import DeleteUser from "./../../assets/delete.svg";
import ViewUser from "./../../assets/show-user.svg";
import GreenButton from "../../components/GreenButton/GreenButton";
import Modals from "../../components/MessageModals/MessageModals";
import CartGif from "./../../assets/cart-gif.gif";
import MoneyGif from "./../../assets/money-gif.gif";
import BlueButton from "../../components/BlueButton/BlueButton";
import ShopImg from "./../../assets/shop-img.png";
import IdImg from "./../../assets/id-img.png";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import api from "../../utils/api";

const Partner = () => {
  const { id } = useParams(); // Get ID from URL params
  const [view, setView] = useState("products");
  const [shopData, setShopData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [currentModalView, setCurrentModalView] = useState("userRates");
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState("");
  const [selectedComment, setSelectedComment] = useState(null); // NEW: hold the clicked comment

  // Fetch shop data
  const getShopData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/vendors/${id}`);
      setShopData(res.data.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getShopData();
    }
  }, [id]);

  // UPDATED: accept optional payload (e.g., a comment row)
  const handleOpen = (view, payload = null) => {
    setCurrentModalView(view);
    if (view === "userRates") {
      setSelectedComment(payload); // store selected comment to pass to drawer
    }
    setIsOpen(true);
  };

  const handleModalOpen = (view) => {
    setCurrentView(view);
    setOpenModal(true);
  };

  const handleClose = () => setIsOpen(false);

  const actionColumn = [
    {
      field: "action",
      headerName: "",
      width: 100,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            <div className="cell-btn">
              {view === "address" ? (
                <img src={AddressLocation} alt="" />
              ) : (
                <img
                  src={ViewUser}
                  alt=""
                  // UPDATED: pass the current row (comment) to the modal open handler
                  onClick={() => handleOpen("userRates", params.row)}
                />
              )}
            </div>

            <div
              className="cell-btn"
              onClick={() => {
                handleModalOpen("delete");
              }}
            >
              <img src={DeleteUser} alt="" />
            </div>
          </div>
        );
      },
    },
  ];

  const renderProductsView = () => {
    return (
      // Added return statement
      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={ProductsColumns}
          rows={
            shopData?.products?.map((row, index) => ({
              ...row,
              index: index + 1,
              id: row.id ?? `${index + 1}`, // ensure id exists
            })) || []
          }
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          rowCount={shopData?.products?.length || 0} // Fixed typo in length
          pageSizeOptions={[10, 25, 50]}
        />
      </div>
    );
  };

  const renderPaymentMethodView = () => {
    // Define bank-specific columns
    const bankColumns = [
      {
        field: "acc_type",
        headerName: "طريقة استلام المستحقات",
        width: 200,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return <span>البطاقات الائتمانية</span>;
        },
      },
      {
        field: "bank_name",
        headerName: "اسم البنك",
        width: 200,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "provider_name",
        headerName: "الاسم",
        width: 200,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "account_no",
        headerName: "رقم الحساب",
        width: 200,
        headerAlign: "center",
        align: "center",
      },
    ];

    const walletColumns = [
      {
        field: "acc_type",
        headerName: "طريقة استلام المستحقات",
        width: 200,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return <span>المحفظة الالكترونية</span>;
        },
      },
      {
        field: "wallet_number",
        headerName: "رقم الموبايل",
        width: 160,
        headerAlign: "center",
        align: "center",
      },
    ];

    // Determine which columns to display based on acc_type (FIXED comparison)
    const accType = (shopData?.acc_type || "").toLowerCase();
    const paymentColumns = accType === "bank" ? bankColumns : walletColumns;

    return (
      <div className="container">
        <DataGrid
          sx={{
            backgroundColor: "white",
            width: "100%",
            "& .MuiDataGrid-columnHeader": { backgroundColor: "#F8FAFC" },
          }}
          autoHeight={true}
          columns={paymentColumns}
          rows={[{ ...(shopData || {}), id: shopData?.id || "shop" }]}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          hideFooter
          rowHeight={60}
        />
      </div>
    );
  };

  const renderRatesView = () => (
    <div className="container">
      <DataGrid
        sx={{ backgroundColor: "white", width: "100%" }}
        autoHeight={true}
        columns={UserRatesColumns.concat(actionColumn)}
        rows={
          shopData?.comments?.map((row, index) => {
            return { ...row, index: index + 1, id: row.id ?? `${index + 1}` };
          }) || []
        }
        disableVirtualization
        disableColumnFilter
        disableColumnMenu
        hideFooter
        rowHeight={60}
      />
    </div>
  );

  const renderPapersView = () => (
    <div className="edit-partner-container">
      <p className="title">بطاقة الهوية - الوجه/الظهر</p>
      <div className="items-container">
        <div className="item">
          <span>صورة الوجه</span>
          <div className="img-container">
            <img src={shopData?.id_front_image || IdImg} alt="" />
          </div>
        </div>

        <div className="item">
          <span>صورة الظهر</span>
          <div className="img-container">
            <img src={shopData?.id_back_image || IdImg} alt="" />
          </div>
        </div>
      </div>
    </div>
  );

  const views = {
    products: renderProductsView,
    paymentMethod: renderPaymentMethodView,
    rates: renderRatesView,
    papers: renderPapersView,
  };

  return (
    <div className="partner">
      <div className="user-header">
        <div className="user-info">
          <div className="info-container">
            <p className="title">
              {shopData?.type === "factory" ? "تفاصيل المصنع" : "تفاصيل المتجر"}
            </p>
            <div className="info">
              <img src={shopData?.logo || ShopImg} alt="" />
              <div className="wrapper">
                <p className="name">{shopData?.name}</p>
                <span className="phone">
                  <StarIcon style={{ color: "#F9A130" }} />
                  <span>{shopData?.rating}</span>
                  <span>({shopData?.number_of_comments})</span>
                </span>
              </div>
            </div>
          </div>

          <div className="widgets">
            <Link className="widget">
              <div
                className="icon-container"
                style={{ backgroundColor: "#ECF9FF" }}
              >
                <img
                  src={FactoriesProductsGif}
                  alt=""
                  className="widget-icon"
                />
              </div>
              <div className="widget-text-container">
                <p className="number">{shopData?.number_of_products || 0}</p>
              </div>
              <span className="widget-title">عدد المنتجات</span>
            </Link>

            <Link className="widget">
              <div
                className="icon-container"
                style={{ backgroundColor: "#ECF9FF" }}
              >
                <img
                  src={FactoriesProductsGif}
                  alt=""
                  className="widget-icon"
                />
              </div>
              <div className="widget-text-container">
                <p className="number">
                  {shopData?.number_of_products_expired || 0}
                </p>
              </div>
              <span className="widget-title">عدد المنتجات المنتهية</span>
            </Link>

            <Link className="widget">
              <div
                className="icon-container"
                style={{ backgroundColor: "#ECF9FF" }}
              >
                <img src={CartGif} alt="" className="widget-icon" />
              </div>
              <div className="widget-text-container">
                <p className="number">{shopData?.number_of_orders || 0}</p>
              </div>
              <span className="widget-title">عدد الطلبات المكتملة</span>
            </Link>

            <Link className="widget">
              <div
                className="icon-container"
                style={{ backgroundColor: "#ECF9FF" }}
              >
                <img src={CartGif} alt="" className="widget-icon" />
              </div>
              <div className="widget-text-container">
                <p className="number">
                  {shopData?.number_of_canceled_orders || 0}
                </p>
              </div>
              <span className="widget-title">عدد الطلبات الملغية</span>
            </Link>

            <Link className="special-widget">
              <div className="special-widget-item">
                <img src={MoneyGif} alt="" className="widget-icon" />
                <div className="widget-text-container">
                  <p className="number">{shopData?.net_profit || 0}</p>
                  <span>صافى الربح</span>
                </div>
              </div>

              <div className="special-widget-item">
                <img src={MoneyGif} alt="" className="widget-icon" />
                <div className="widget-text-container">
                  {/* <p className="number">{shopData?.net_profit_admin || 0}</p> */}
                  <p className="number">{shopData?.app_percentage || 0}</p>
                  <span>عمولة التطبيق</span>
                </div>
              </div>
            </Link>

            <Link
              className="widget"
              style={{
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3 style={{ color: "#7EA91A" }}>{shopData?.wallet || 0} جنية</h3>
              <h6 style={{ color: "#64748B" }}>المحفظة</h6>
              <BlueButton
                size="full"
                text="تسوية الرصيد"
                variant="fill"
                onClick={() => handleModalOpen("withdrawal")}
              />
            </Link>
          </div>
        </div>

        <div className="btns-container">
          <GreenButton
            text="المنتجات"
            size="small"
            variant={view === "products" ? "fill" : "outlined"}
            onClick={() => setView("products")}
          />

          <GreenButton
            text="الاوراق"
            size="small"
            variant={view === "papers" ? "fill" : "outlined"}
            onClick={() => setView("papers")}
          />

          <GreenButton
            text="بيانات الدفع"
            size="medium"
            variant={view === "paymentMethod" ? "fill" : "outlined"}
            onClick={() => setView("paymentMethod")}
          />

          <GreenButton
            text="التقييمات"
            size="small"
            variant={view === "rates" ? "fill" : "outlined"}
            onClick={() => setView("rates")}
          />
        </div>
      </div>

      {views[view]()}

      <Modals
        open={openModal}
        currentView={currentView}
        onClose={() => {
          setOpenModal(false);
        }}
        route={"/balance-settlement"}
        onDataChange={getShopData}
        text="تم تسوية الرصيد بنجاح"
        title="تسوية الرصيد"
        vendor_id={id}
      />

      <SwipeableModals
        open={isOpen}
        onClose={handleClose}
        onOpen={() => setIsOpen(true)}
        currentView={currentModalView}
        // NEW: pass the full comments list (optional) and the selected comment
        comments={shopData?.comments || []}
        commentData={selectedComment}
      />
    </div>
  );
};

export default Partner;
